import React, { useState, useEffect, useCallback } from 'react';
import { X, Maximize2 } from 'lucide-react';
import {
    TavolaAnatomica, SelettoreTavola, tavolaIniziale, etichettaTavola, puntiDellaTavola,
} from './TavolaAnatomica';
import '../../styles/TavolaAnatomica.css';
import '../../styles/MappaZone.css';
import '../../styles/MappaZoneConsole.css';
import { notificaAvviso } from '../../utils/notifiche';
import { mlLeggibili } from './ProdottiProcedura';

/**
 * MappaZone
 * Schema tecnico su tavola anatomica: segna DOVE si e' intervenuti
 * durante questa procedura.
 *
 * Diverso dalle annotazioni dell'anamnesi, che descrivono lo stato del
 * paziente e restano valide nel tempo. Qui i punti appartengono alla
 * singola seduta: le zone trattate a marzo non devono comparire sullo
 * schema di settembre.
 *
 * OGNI PUNTO PORTA LA SUA TAVOLA (`mappa`), e si disegna solo su
 * quella: la tavola e il disegno stanno in TavolaAnatomica, che e' la
 * stessa dell'anamnesi. Fino al 21 settembre 2026 la tavola era una
 * sola per tutto il modulo e i punti restavano visibili cambiandola:
 * due punti sul viso comparivano a mezz'aria accanto alla figura
 * intera. I punti salvati prima di allora non hanno una tavola: si
 * mostrano nell'elenco come «da assegnare», con il modo per assegnarli
 * a quella in vista, e su nessuna tavola finche' qualcuno non lo fa -
 * assegnarli da soli sarebbe inventare un dato clinico.
 *
 * LA NUMERAZIONE E' UNICA per tutta la procedura, e la tabella sotto
 * mostra TUTTE le righe, con la colonna «Tavola»: una quantita' di
 * prodotto che sparisce dalla vista e' peggio di una riga in piu'.
 *
 * I PRODOTTI DELLA PROCEDURA (filler, migration 324). Quando lo schema e'
 * di una procedura iniettabile, `prodotti` sono i suoi prodotti non tolti
 * (colore, ml, lotto). Ogni punto porta allora il `prodotto_id`, e il suo
 * colore e' quello del prodotto: si tocca un prodotto nella legenda per
 * attivarlo, e i punti nuovi sono suoi. La numerazione resta UNA per tutta
 * la procedura; il colore accompagna il numero, non lo sostituisce, cosi'
 * lo schema stampato in bianco e nero si legge lo stesso. I punti segnati
 * prima, col prodotto scritto a mano, restano come sono e si leggono
 * «prodotto non collegato»: collegarli da soli vorrebbe dire inventare a
 * quale prodotto apparteneva un testo libero. I ml stanno sul prodotto
 * (legenda), non sul punto: la quantita' del punto resta una nota.
 * Il server rifiuta un punto con un prodotto che la procedura non ha.
 *
 * Non salva nulla da solo: restituisce { mappa, punti } al modulo che
 * lo contiene, come facevano MappaBotox e MappaFiller (tolti il 22 settembre
 * 2026). `mappa` e' la
 * tavola in vista all'ultimo salvataggio, per riaprire dov'era.
 */

const COLORE_SCOLLEGATO = 'var(--rm-testo-tenue)';

const MappaZone = ({ data, onChange, readOnly, sessoPaziente, onFermo, prodotti }) => {
    const collegato = Array.isArray(prodotti);
    const perProdotto = new Map((prodotti || []).map((x) => [Number(x.prodotto_id), x]));
    const [scelto, setScelto] = useState(null);
    /* Il prodotto attivo: quello toccato nella legenda, se c'e' ancora;
       altrimenti il primo. */
    const attivo = collegato
        ? (perProdotto.has(Number(scelto)) ? Number(scelto) : (prodotti[0] ? Number(prodotti[0].prodotto_id) : null))
        : null;
    const prodottoDi = (p) => (p.prodotto_id != null ? perProdotto.get(Number(p.prodotto_id)) : null);
    /* Modulo nuovo: la tavola parte dal sesso in anagrafica. Modulo
       gia' salvato: vale la tavola dell'ultimo lavoro. */
    const [mappa, setMappa] = useState(data?.mappa || tavolaIniziale(sessoPaziente));
    const [punti, setPunti] = useState(data?.punti || []);
    const [inModifica, setInModifica] = useState(null);
    const [ingrandita, setIngrandita] = useState(false);

    useEffect(() => {
        if (data) {
            if (data.mappa) setMappa(data.mappa);
            setPunti(data.punti || []);
        }
    }, [data]);

    // Esc chiude l'ingrandimento: e' il gesto che tutti si aspettano.
    const chiudiConEsc = useCallback((e) => {
        if (e.key === 'Escape') setIngrandita(false);
    }, []);

    useEffect(() => {
        if (!ingrandita) return;
        document.addEventListener('keydown', chiudiConEsc);
        return () => document.removeEventListener('keydown', chiudiConEsc);
    }, [ingrandita, chiudiConEsc]);

    const emetti = (nuovaMappa, nuoviPunti) => {
        if (onChange) onChange({ mappa: nuovaMappa, punti: nuoviPunti });
    };
    /* «Fermo»: il contenuto e' in uno stato da salvare. Non a ogni tasto
       (onChange lo fa gia'), ma quando un punto nasce o se ne va, quando
       cambia la tavola, e quando si esce da un campo di testo: e' il
       momento in cui il modulo si salva da solo (regola di Alex del
       24/09/2026). */
    const fermo = (nuovaMappa, nuoviPunti) => {
        if (onFermo) onFermo({ mappa: nuovaMappa, punti: nuoviPunti });
    };

    const cambiaMappa = (nuova) => {
        setMappa(nuova);
        emetti(nuova, punti);
        fermo(nuova, punti);
    };

    const aggiungiPunto = ({ x, y }) => {
        if (readOnly) return;
        if (collegato && !attivo) {
            notificaAvviso('Aggiungi prima un prodotto alla procedura, poi segna i punti sullo schema.');
            return;
        }
        const nuovo = collegato
            ? { id: Date.now(), mappa, x, y, prodotto_id: attivo, quantita: '' }
            : { id: Date.now(), mappa, x, y, prodotto: '', quantita: '' };
        const nuoviPunti = [...punti, nuovo];
        setPunti(nuoviPunti);
        setInModifica(nuovo.id);
        emetti(mappa, nuoviPunti);
        fermo(mappa, nuoviPunti);
    };

    const aggiornaPunto = (id, campo, valore) => {
        const nuoviPunti = punti.map((p) => (p.id === id ? { ...p, [campo]: valore } : p));
        setPunti(nuoviPunti);
        emetti(mappa, nuoviPunti);
    };

    const rimuoviPunto = (id) => {
        const nuoviPunti = punti.filter((p) => p.id !== id);
        setPunti(nuoviPunti);
        if (inModifica === id) setInModifica(null);
        emetti(mappa, nuoviPunti);
        fermo(mappa, nuoviPunti);
    };

    const totale = punti.reduce((s, p) => s + (parseFloat(String(p.quantita).replace(',', '.')) || 0), 0);
    const inVista = puntiDellaTavola(punti, mappa).length;
    const daAssegnare = punti.filter((p) => !p.mappa).length;

    const rendiPunto = (p, i) => {
        const pr = collegato ? prodottoDi(p) : null;
        const nome = pr ? pr.prodotto : p.prodotto;
        return (
            <span
                className={`mz-punto ${inModifica === p.id ? 'mz-punto-attivo' : ''}`}
                style={collegato ? { background: pr ? pr.colore : COLORE_SCOLLEGATO } : undefined}
                onClick={(e) => { e.stopPropagation(); if (!readOnly) setInModifica(p.id); }}
                title={nome ? `${nome}${p.quantita ? ` · ${p.quantita}` : ''}` : `Punto ${i + 1}`}
                data-punto-prodotto={collegato ? (pr ? pr.prodotto_id : 'scollegato') : undefined}
            >
                {i + 1}
            </span>
        );
    };

    /* La legenda: un prodotto per riga, col colore, i ml, il lotto se la
       merce e' uscita e i numeri dei suoi punti. In modifica si tocca per
       attivarlo. */
    const numeriDi = (prodottoId) => punti
        .map((p, i) => (Number(p.prodotto_id) === Number(prodottoId) ? i + 1 : null))
        .filter((x) => x !== null);
    const scollegati = collegato ? punti.map((p, i) => (prodottoDi(p) ? null : i + 1)).filter((x) => x !== null) : [];
    const legenda = collegato && (prodotti.length > 0 || scollegati.length > 0) && (
        <div className="mz-legenda" aria-label="Legenda dei prodotti">
            {prodotti.map((x) => {
                const numeri = numeriDi(x.prodotto_id);
                const contenuto = (
                    <>
                        <span className="mz-legenda-colore" style={{ background: x.colore }} />
                        <span className="mz-legenda-nome">{x.prodotto}</span>
                        <span className="mz-legenda-dato">{mlLeggibili(x.ml)} ml</span>
                        {x.lotti && <span className="mz-legenda-dato">lotto {x.lotti}</span>}
                        <span className="mz-legenda-dato">
                            {numeri.length ? `punti ${numeri.join(', ')}` : 'nessun punto'}
                        </span>
                    </>
                );
                return readOnly ? (
                    <div key={x.prodotto_id} className="mz-legenda-voce">{contenuto}</div>
                ) : (
                    <button key={x.prodotto_id} type="button" aria-pressed={attivo === Number(x.prodotto_id)}
                            className={`mz-legenda-voce ${attivo === Number(x.prodotto_id) ? 'mz-legenda-attiva' : ''}`}
                            title="Tocca per segnare i punti di questo prodotto"
                            onClick={() => setScelto(Number(x.prodotto_id))}>
                        {contenuto}
                    </button>
                );
            })}
            {scollegati.length > 0 && (
                <div className="mz-legenda-voce">
                    <span className="mz-legenda-colore" style={{ background: COLORE_SCOLLEGATO }} />
                    <span className="mz-legenda-nome">Prodotto non collegato</span>
                    <span className="mz-legenda-dato">punti {scollegati.join(', ')}</span>
                </div>
            )}
        </div>
    );

    /* La tavola compare due volte, in linea e ingrandita: stesso
       componente, stessi punti, stesse percentuali dell'immagine. */
    const tavola = (grande) => (
        <div className={`mz-tavola ${readOnly ? 'mz-sola-lettura' : ''} ${grande ? 'mz-tavola-grande' : ''}`}>
            <TavolaAnatomica mappa={mappa} punti={punti} rendiPunto={rendiPunto}
                             onClick={readOnly ? null : aggiungiPunto} />
        </div>
    );

    return (
        <div className="mz-wrap">

            <div className="mz-comandi">
                {!readOnly && <SelettoreTavola mappa={mappa} onCambia={cambiaMappa} className="mz-" />}
                <button className="mz-ingrandisci" onClick={() => setIngrandita(true)}
                        title="Apri la tavola a schermo intero">
                    <Maximize2 size={14} strokeWidth={1.8} aria-hidden="true" />
                    Ingrandisci
                </button>
                <span className="mz-conteggio">
                    {inVista === 0 ? 'Nessun punto su questa tavola'
                        : inVista === 1 ? '1 punto su questa tavola' : `${inVista} punti su questa tavola`}
                    {punti.length > inVista && ` · ${punti.length} in tutto`}
                </span>
            </div>

            {tavola(false)}
            {legenda}

            <div className="mz-elenco">
                {punti.length === 0 ? (
                    <p className="mz-vuoto">
                        {readOnly
                            ? 'Nessuna zona segnata.'
                            : 'Tocca la tavola nel punto in cui sei intervenuto.'}
                    </p>
                ) : (
                    <>
                        {daAssegnare > 0 && (
                            <p className="mz-avviso">
                                {daAssegnare === 1 ? 'Un punto è' : `${daAssegnare} punti sono`} senza tavola:
                                {' '}segnati prima del 21 settembre 2026, quando la tavola non veniva
                                registrata. Non si disegnano finché non li assegni tu a quella giusta.
                            </p>
                        )}
                        <table className="mz-tabella">
                            <thead>
                                <tr>
                                    <th>N.</th>
                                    <th>Tavola</th>
                                    <th>{collegato ? 'Prodotto' : 'Prodotto o tecnica'}</th>
                                    <th>Quantità</th>
                                    {!readOnly && <th />}
                                </tr>
                            </thead>
                            <tbody>
                                {punti.map((p, i) => (
                                    <tr key={p.id}
                                        className={`${inModifica === p.id ? 'mz-riga-attiva' : ''} ${p.mappa === mappa ? '' : 'mz-riga-altrove'}`}>
                                        <td className="mz-numero">{i + 1}</td>
                                        <td className="mz-tavola-cella">
                                            {p.mappa ? (
                                                <button type="button" className="mz-vai" disabled={p.mappa === mappa}
                                                        title={p.mappa === mappa ? 'In vista' : 'Mostra questa tavola'}
                                                        onClick={() => cambiaMappa(p.mappa)}>
                                                    {etichettaTavola(p.mappa)}
                                                </button>
                                            ) : readOnly ? (
                                                <span className="mz-da-assegnare">Tavola: da assegnare</span>
                                            ) : (
                                                <button type="button" className="mz-vai mz-da-assegnare"
                                                        title="Assegna questo punto alla tavola in vista"
                                                        onClick={() => aggiornaPunto(p.id, 'mappa', mappa)}>
                                                    Da assegnare → {etichettaTavola(mappa)}
                                                </button>
                                            )}
                                        </td>
                                        <td>
                                            {collegato ? (prodottoDi(p) ? (
                                                readOnly ? (
                                                    <span className="mz-prodotto">
                                                        <span className="mz-legenda-colore" style={{ background: prodottoDi(p).colore }} />
                                                        {prodottoDi(p).prodotto}
                                                    </span>
                                                ) : (
                                                    <select value={String(p.prodotto_id)} aria-label={`Prodotto del punto ${i + 1}`}
                                                            onFocus={() => setInModifica(p.id)}
                                                            onChange={(e) => {
                                                                const nuoviPunti = punti.map((q) => (q.id === p.id
                                                                    ? { ...q, prodotto_id: Number(e.target.value) } : q));
                                                                setPunti(nuoviPunti);
                                                                emetti(mappa, nuoviPunti);
                                                                fermo(mappa, nuoviPunti);
                                                            }}>
                                                        {prodotti.map((x) => (
                                                            <option key={x.prodotto_id} value={String(x.prodotto_id)}>{x.prodotto}</option>
                                                        ))}
                                                    </select>
                                                )
                                            ) : (
                                                <span className="mz-scollegato">
                                                    {p.prodotto ? `${p.prodotto} · ` : ''}prodotto non collegato
                                                </span>
                                            )) : readOnly ? (p.prodotto || '—') : (
                                                <input
                                                    type="text"
                                                    value={p.prodotto}
                                                    onChange={(e) => aggiornaPunto(p.id, 'prodotto', e.target.value)}
                                                    onBlur={() => fermo(mappa, punti)}
                                                    onFocus={() => setInModifica(p.id)}
                                                    placeholder="Es. acido ialuronico"
                                                />
                                            )}
                                        </td>
                                        <td>
                                            {readOnly ? (p.quantita || '—') : (
                                                <input
                                                    type="text"
                                                    value={p.quantita}
                                                    onChange={(e) => aggiornaPunto(p.id, 'quantita', e.target.value)}
                                                    onBlur={() => fermo(mappa, punti)}
                                                    onFocus={() => setInModifica(p.id)}
                                                    placeholder="Es. 0,5 ml"
                                                    className="mz-quantita"
                                                />
                                            )}
                                        </td>
                                        {!readOnly && (
                                            <td>
                                                <button className="mz-rimuovi"
                                                        onClick={() => rimuoviPunto(p.id)}
                                                        title="Rimuovi il punto">
                                                    <X size={14} strokeWidth={2} aria-hidden="true" />
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {!collegato && totale > 0 && (
                            <div className="mz-totale">
                                <span>Totale</span>
                                <strong>{totale.toFixed(1).replace('.', ',')}</strong>
                            </div>
                        )}
                    </>
                )}
            </div>

            {ingrandita && (
                <div className="mz-sovrapposizione" onClick={() => setIngrandita(false)}>
                    <div className="mz-sovrapposizione-corpo" onClick={(e) => e.stopPropagation()}>
                        <div className="mz-sovrapposizione-testa">
                            <span>
                                {etichettaTavola(mappa)}
                                {!readOnly && ' — tocca per aggiungere un punto'}
                            </span>
                            <button onClick={() => setIngrandita(false)} aria-label="Chiudi">
                                <X size={18} strokeWidth={1.8} />
                            </button>
                        </div>
                        {tavola(true)}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MappaZone;
